// VERA-246: icon registry for company addresses.
//
// "Office", "Store", "Production" and "Distributor" addresses have fixed icons, and
// those are the icons the create/edit modal shows on the matching type chip — the two
// must agree, or a type is picked under one glyph and rendered under another. The
// "Custom" type
// (AddressType 'other') lets the user pick one of the curated icons below, whose
// stable string `key` is persisted on `address.icon`. `getAddressIcon` resolves an
// address to its lucide component so every display surface renders it consistently.

import {
  StoreIcon,
  FactoryIcon,
  BuildingIcon,
  Building2Icon,
  WarehouseIcon,
  HomeIcon,
  LandmarkIcon,
  HotelIcon,
  MapPinIcon,
  MapIcon,
  NavigationIcon,
  GlobeIcon,
  BriefcaseIcon,
  PackageIcon,
  BoxIcon,
  ShoppingBagIcon,
  ShoppingCartIcon,
  TruckIcon,
  ShipIcon,
  PlaneIcon,
  AnchorIcon,
  RocketIcon,
  GemIcon,
  CrownIcon,
  StarIcon,
  HeartIcon,
  FlagIcon,
  PaletteIcon,
  ScissorsIcon,
  ShirtIcon,
  WrenchIcon,
  HammerIcon,
  CoffeeIcon,
  UtensilsIcon,
  WineIcon,
  CastleIcon,
  ChurchIcon,
  SchoolIcon,
  GraduationCapIcon,
  TentIcon,
  TreePineIcon,
  TreePalmIcon,
  MountainIcon,
  SunIcon,
  SnowflakeIcon,
  ZapIcon,
  LeafIcon,
  Flower2Icon,
  type LucideIcon,
} from 'lucide-react'

import type { AddressType } from 'types'

export type AddressIconOption = { key: string; label: string; Icon: LucideIcon }

// Curated set offered in the Custom-address icon picker. `key` is the persisted
// value — keep it stable (renaming a key orphans existing rows). `label` is only
// used for the picker's text search.
export const ADDRESS_ICON_OPTIONS: AddressIconOption[] = [
  { key: 'Building', label: 'Building', Icon: BuildingIcon },
  { key: 'Building2', label: 'Building office', Icon: Building2Icon },
  { key: 'Store', label: 'Store shop', Icon: StoreIcon },
  { key: 'Factory', label: 'Factory production', Icon: FactoryIcon },
  { key: 'Warehouse', label: 'Warehouse depot', Icon: WarehouseIcon },
  { key: 'Home', label: 'Home house', Icon: HomeIcon },
  { key: 'Landmark', label: 'Landmark bank', Icon: LandmarkIcon },
  { key: 'Hotel', label: 'Hotel', Icon: HotelIcon },
  { key: 'MapPin', label: 'Map pin location', Icon: MapPinIcon },
  { key: 'Map', label: 'Map', Icon: MapIcon },
  { key: 'Navigation', label: 'Navigation', Icon: NavigationIcon },
  { key: 'Globe', label: 'Globe world', Icon: GlobeIcon },
  { key: 'Briefcase', label: 'Briefcase office work', Icon: BriefcaseIcon },
  { key: 'Package', label: 'Package parcel', Icon: PackageIcon },
  { key: 'Box', label: 'Box', Icon: BoxIcon },
  { key: 'ShoppingBag', label: 'Shopping bag retail', Icon: ShoppingBagIcon },
  { key: 'ShoppingCart', label: 'Shopping cart', Icon: ShoppingCartIcon },
  { key: 'Truck', label: 'Truck delivery shipping', Icon: TruckIcon },
  { key: 'Ship', label: 'Ship boat port', Icon: ShipIcon },
  { key: 'Plane', label: 'Plane airport', Icon: PlaneIcon },
  { key: 'Anchor', label: 'Anchor harbor', Icon: AnchorIcon },
  { key: 'Rocket', label: 'Rocket', Icon: RocketIcon },
  { key: 'Gem', label: 'Gem jewelry diamond', Icon: GemIcon },
  { key: 'Crown', label: 'Crown luxury', Icon: CrownIcon },
  { key: 'Star', label: 'Star', Icon: StarIcon },
  { key: 'Heart', label: 'Heart', Icon: HeartIcon },
  { key: 'Flag', label: 'Flag', Icon: FlagIcon },
  { key: 'Palette', label: 'Palette art design studio', Icon: PaletteIcon },
  { key: 'Scissors', label: 'Scissors salon', Icon: ScissorsIcon },
  { key: 'Shirt', label: 'Shirt apparel clothing', Icon: ShirtIcon },
  { key: 'Wrench', label: 'Wrench repair service', Icon: WrenchIcon },
  { key: 'Hammer', label: 'Hammer workshop', Icon: HammerIcon },
  { key: 'Coffee', label: 'Coffee cafe', Icon: CoffeeIcon },
  { key: 'Utensils', label: 'Utensils restaurant food', Icon: UtensilsIcon },
  { key: 'Wine', label: 'Wine bar', Icon: WineIcon },
  { key: 'Castle', label: 'Castle', Icon: CastleIcon },
  { key: 'Church', label: 'Church', Icon: ChurchIcon },
  { key: 'School', label: 'School', Icon: SchoolIcon },
  { key: 'GraduationCap', label: 'University education', Icon: GraduationCapIcon },
  { key: 'Tent', label: 'Tent camp popup', Icon: TentIcon },
  { key: 'TreePine', label: 'Tree pine nature', Icon: TreePineIcon },
  { key: 'TreePalm', label: 'Palm tree beach resort', Icon: TreePalmIcon },
  { key: 'Mountain', label: 'Mountain', Icon: MountainIcon },
  { key: 'Sun', label: 'Sun', Icon: SunIcon },
  { key: 'Snowflake', label: 'Snowflake winter cold', Icon: SnowflakeIcon },
  { key: 'Zap', label: 'Zap energy power', Icon: ZapIcon },
  { key: 'Leaf', label: 'Leaf eco green', Icon: LeafIcon },
  { key: 'Flower2', label: 'Flower floral', Icon: Flower2Icon },
]

const ADDRESS_ICON_MAP: Record<string, LucideIcon> = Object.fromEntries(
  ADDRESS_ICON_OPTIONS.map(o => [o.key, o.Icon]),
)

// Default icon for a Custom address whose icon hasn't been chosen (or is unknown).
export const DEFAULT_CUSTOM_ADDRESS_ICON = BuildingIcon

/** Resolve an address's type + stored icon key to the lucide component to render. */
export const getAddressIcon = (
  type: AddressType,
  iconKey?: string | null,
): LucideIcon => {
  if (type === 'office') return BriefcaseIcon
  if (type === 'store') return StoreIcon
  if (type === 'production') return FactoryIcon
  if (type === 'distributor') return TruckIcon
  // Custom ('other'): the user-picked icon, falling back to a neutral building.
  return (iconKey && ADDRESS_ICON_MAP[iconKey]) || DEFAULT_CUSTOM_ADDRESS_ICON
}
